import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import { getDataLayerUtilityV4 } from "@tCentro/Custom/Utilities/getDataLayerUtility";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface WishListCardStyles {
    listLineImages?: LazyImageProps;
    gridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImagesEmptyGridItem?: GridItemProps;
    countSpan?: InjectableCss;
}

export const wishListCardStyles: WishListCardStyles = {
    listLineImages: {
        css: css`
            img {
                width: 64px !important;
                height: 64px !important;
                object-fit: contain;
            }
        `,
    },
    countSpan: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            width: 64px;
            height: 64px;
            margin: 20px 0px 20px 0px;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
        `,
    },
    gridContainer: {
        gap: 5,
        css: css`
            > div[class^="GridOffset"] {
                gap: 16px;
                align-items: center;
            }
        `,
    },
    detailLinkGridItem: {
        width: 12,
        css: css`
            > a {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.base};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },

    productImageLink: {
        css: css`
            margin-right: 8px;
        `,
    },
    productImagesGridItem: {
        css: css`
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            width: 64px;
            height: 64px;
            a {
                width: 64px;
                height: 64px;
            }
            span {
                width: 64px;
                height: 64px;
                min-width: 64px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    productImagesEmptyGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const MyListWishListCard = ({ language, extendedStyles, wishList }: Props) => {
    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    let lastUpdatedDisplay = `${translate("Updated")} ${updatedOnDisplay}`;
    if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
        lastUpdatedDisplay += ` ${translate("by")} ${wishList.updatedByDisplayName}`;
    }

    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));

    return (
        <>
            <GridContainer {...styles.gridContainer} data-test-selector="wishListCard">
                <GridItem {...styles.detailLinkGridItem}>
                    <MyListsDetailsPageTypeLink
                        title={wishList.name}
                        wishListId={wishList.id}
                        testSelector="wishListCardName"
                    />
                </GridItem>
                <GridItem {...styles.productImagesGridItem} data-test-selector="productImagesGridItem">
                    {wishList.wishListLineCollection && wishList.wishListLineCollection.length > 0 ? (
                        <>
                            {wishList.wishListLineCollection.slice(0, 3).map((line, index) => {


                                // const onProductClickHandler = () => {
                                //     const dataLayerObject = {
                                //         "item_id": line?.erpNumber || line?.manufacturerItem || "",
                                //         "item_name": line?.shortDescription || "",
                                //         "item_brand": line?.brand?.name || "",
                                //         "price": 1111,
                                //         "quantity": line?.qtyOrdered ?? 1,
                                //         "item_list_id": "my_account_my_lists_details",
                                //         "item_list_name": "My Account: My Lists Details"
                                //     }
                                    // dataLayerObject && getDataLayerUtilityV4(dataLayerObject)
                                // }
                                return (
                                    <>
                                        <Link
                                            key={line.id.toString()}
                                            {...styles.productImageLink}
                                            href={line.productUri}
                                            // onClick={onProductClickHandler}
                                            style={{ boxShadow: "2px 2px 8px 0px #26252529, 0px 0px 1px 0px #2625258F" }}
                                        >
                                            <LazyImage
                                                {...styles.listLineImages}
                                                src={line.smallImagePath}
                                                altText={line.altText}
                                                title={line.altText}
                                            />
                                        </Link>
                                    </>
                                )
                            })}
                            <Typography {...styles.countSpan}>
                                {wishList.properties?.remainingProductsCount != null &&
                                    `+${wishList.properties.remainingProductsCount}`}
                            </Typography>
                        </>
                    ) : (
                        <GridItem {...styles.productImagesEmptyGridItem}></GridItem>
                    )}
                </GridItem>
            </GridContainer>
        </>
    );
};

export default connect(mapStateToProps)(MyListWishListCard);
